/** Data-sync support endpoints: remote product catalogs + tour mappings
 *  (Additional_Ext_ID) and the booking-sync target picker. */
import { apiDelete, apiGet, apiPatch, apiPost, ConsoleApiError } from './client';
import type { components } from './types';

/** Why a tenant/account cannot import the listed catalogue (`code` is the
 *  contract: `product_sync_unsupported` | `catalogue_sync_disabled`). */
export type ImportBlocker = components['schemas']['ConsoleImportBlocker'];

// ---------------------------------------------------------------------------
// Products (remote catalog + cross-references)
// ---------------------------------------------------------------------------

export interface ProductChild {
  id: string;
  name: string;
  kind?: string;
  /** What differs between the children of ONE product — never what they share
   *  with their parent, which the product's own facets already say. Optional:
   *  most restechs' children carry nothing but an id and a title. */
  facets?: ProductFacet[];
}

/** An integration-supplied feature that helps tell apart two products sharing a
 *  name. The label vocabulary belongs to the restech — render, never interpret. */
export interface ProductFacet {
  label: string;
  value: string;
}

export interface ProductListItem {
  id: string | null;
  name: string;
  children: ProductChild[];
  /** What the tenant's product template mints for this product, rendered
   *  server-side at fetch time — the only point the raw payload exists. */
  template_id?: string | null;
  /** Why the template could not render for this product. Reported rather than
   *  skipped: a run that quietly covered fewer products is worse than one that
   *  says which it could not do. */
  template_error?: string | null;
  /** Ordered most- to least-identifying. Absent facets are omitted by the
   *  backend, so length varies per row. Optional: rows cached before the
   *  facets deploy have no key at all. */
  facets?: ProductFacet[];
}

/** What an existing mapping turned out to be, judged against the fetched
 *  catalogue rather than against the string. A ventrata product id and an
 *  option id are both bare UUIDs, so nothing else can separate them. */
export interface MappingClassification {
  /** A policy id, or null when the catalogue cannot explain the value. */
  policy: string | null;
  product_id: string | null;
  option_id: string | null;
  /** More than one policy fits: bokun's `{product}/{x}` may be a rate or a
   *  ticket, and the listing carries neither. */
  ambiguous: boolean;
  /** Always set when `policy` is null — say why, never guess. */
  reason: string | null;
}

export interface ProductMapping {
  via: 'custom_id' | 'ext_id';
  custom_id: string;
  tour: { id: number; name: string | null };
  policy: MappingClassification;
  ext_pk?: number;
  id_type?: string;
  api_queryable?: boolean;
  use_for_matching?: boolean;
  restech_account?: string | null;
  comment?: string | null;
  ext_link?: string | null;
}

/** Which flow a policy belongs to.
 *
 *  `catalogue` — a shape the vendor's catalogue actually has (a product, its
 *  options), so a product sync can create it.
 *  `matching`  — a booking-time qualifier (a departure time, a ticket type)
 *  that an operator adds by hand in the external-ID grid. A catalogue entry
 *  does not have ONE departure time, it has many.
 *
 *  `catalogue` is always a subset of `matching`: a tour keyed on a string the
 *  matcher cannot emit never binds to a booking. */
export type PolicyFlow = 'catalogue' | 'matching';

/** One entry of the restech's policy catalogue. */
export interface MappingPolicy {
  id: string;
  label: string;
  /** e.g. `{product}/{HH:MM}` — what the id looks like. */
  form: string;
  description: string;
  /** Set only where a policy has a property worth warning about. */
  caveat: string | null;
  id_type: string;
  /** The full list is returned so an EXISTING mapping can be named whatever it
   *  is; the product-sync UI shows only the `catalogue` ones. */
  flows: PolicyFlow[];
  /** The id is built by a tenant-configured template, so `form` is a label
   *  rather than a literal shape. */
  templated: boolean;
  /** Creatable here and now. Narrower than `flows` includes `catalogue`:
   *  the product template also needs a template to be configured. */
  mintable: boolean;
}

/** Why a listing came back empty when the integration knows the cause without
 *  asking the vendor — the fetch made no HTTP call at all. Null on every
 *  successful fetch, and the response is still 200 with `count: 0`.
 *
 *  `code` is the contract; the message text is not. Branch on the code, render
 *  `detail` verbatim (the backend writes it for an operator to read). */
export interface ProductListReason {
  /** `fareharbor_user_key_not_a_company_map` | `fareharbor_no_companies`, and
   *  whatever later integrations add — treat it as an open vocabulary. */
  code: string;
  /** One sentence an operator can act on. Safe to render as-is. */
  detail: string;
  /** The integration-config keys that have to change, for pointing at the
   *  field rather than describing it. */
  config_keys?: string[];
}

export interface ProductsResponse {
  integration: string;
  account: string | null;
  features: string[];
  /** False only when the caller passed the `task_id` of the fetch that
   *  produced these rows — the listing it just watched being fetched. */
  from_cache: boolean;
  fetched_ts: string;
  /** The `list_products` background task that fetched these rows — open it
   *  for the fetch log. Null on a blocked listing (nothing was fetched). */
  fetch_task_id: string | null;
  /** The fetch stopped at `limit` rows (a runaway-pagination guard, not a
   *  business limit): products past it are missing and their mappings show as
   *  unexplained. The page must say so. */
  truncated: boolean;
  limit: number;
  count: number;
  products: ProductListItem[];
  /** Keyed by the CATALOGUE PRODUCT the mapping belongs to — not by the
   *  mapping's own value, which for an option policy is the option id. */
  mappings: Record<string, ProductMapping[]>;
  /** Mappings no product in the listing explains: a delisted product, or a
   *  hand-written id. Capped by the backend. */
  unexplained_mappings: ProductMapping[];
  policies: MappingPolicy[];
  /** The tenant's configured policy, or null when they must choose (bokun). */
  default_policy: string | null;
  /** The configured `product_mapping_id_template`, if any. */
  product_template: string | null;
  /** The booking-side templates it must be paired with — one renders against a
   *  product payload, the other against a booking payload, and they have to
   *  produce the same string or the tours never match. */
  matching_templates: string[];
  /** Set when the product template has no booking half. The page still loads;
   *  the template policy is simply unusable until it is fixed. */
  template_pairing_error: string | null;
  /** Null (or absent, on a response cached before the field shipped) after any
   *  successful fetch. Set means there was never a catalogue to fetch — render
   *  the guidance instead of an empty table. */
  reason?: ProductListReason | null;
  /** Whether this tenant/account may import the listed products, computed on
   *  every read. The page gates the launch on THIS, never on the integration's
   *  `supports`: a gated restech (Ventrata) lists `product_sync` but imports
   *  only where the tenant opted in. Absent only from a backend older than the
   *  field (then the integration's features decide, as before). */
  can_import?: boolean;
  /** Why `can_import` is false; null when it is true. */
  import_blocker?: ImportBlocker | null;
}

// ---------------------------------------------------------------------------
// Bulk mapping under a policy
// ---------------------------------------------------------------------------

export interface ApplyMappingsInput {
  tour_operator_id: number;
  integration: string;
  account?: string | null;
  policy: string;
  items: { product_id: string; tour_id: number }[];
  /** What to do about a product already mapped some other way. `skip` leaves
   *  it alone; `parallel` adds the new mapping so both ids resolve; `rewire`
   *  adds the new one and retires the old. */
  on_conflict?: 'skip' | 'parallel' | 'rewire';
  /** How `rewire` retires the old mapping. `park` sets use_for_matching=false,
   *  which since 2026-09-21 stops booking matching AND XML import — inert but
   *  reversible. `delete` removes the row. */
  rewire_old?: 'park' | 'delete';
  dry_run?: boolean;
}

export interface ApplyMappingsResult {
  policy: string;
  dry_run: boolean;
  on_conflict: 'skip' | 'parallel' | 'rewire';
  rewire_old: 'park' | 'delete';
  created: {
    product_id: string;
    custom_id: string;
    option_id: string | null;
    tour_id: number;
    ext_pk?: number;
  }[];
  skipped: { product_id: string; custom_id: string; existing_policy: string | null }[];
  conflicts: { product_id: string; custom_id: string; existing_policy: string | null }[];
  /** Mappings stood down by `rewire`. */
  retired: {
    product_id: string;
    custom_id: string;
    ext_pk: number;
    was_policy: string | null;
    action: 'park' | 'delete';
  }[];
  errors: { product_id: string; custom_id?: string; detail: string }[];
}

export async function applyMappings(body: ApplyMappingsInput): Promise<ApplyMappingsResult> {
  return apiPost('/ops/sync_products/apply_mappings/', body);
}

export interface ProductScope {
  tour_operator_id: number;
  integration: string;
  account?: string | null;
}

/** What loading a catalogue answered: the listing now (a cache hit, or an
 *  account the integration says it cannot list — `listing.reason`), or the
 *  `list_products` task fetching it in the background. `adopted` = that task
 *  was already running for this account (one fetch per account). */
export type ProductFetchResult =
  | { status: 'ready'; listing: ProductsResponse }
  | { status: 'pending'; task_id: string; state: number; adopted: boolean };

/** Load the catalogue. Never waits for the vendor: without `force` a cached
 *  listing comes back at once; otherwise a background task fetches it — watch
 *  the task, then read the result with `getProducts({..., task_id})`. */
export async function startProductFetch(
  params: ProductScope & { force?: boolean },
): Promise<ProductFetchResult> {
  return apiPost('/ops/sync_products/fetch/', {
    tour_operator_id: params.tour_operator_id,
    integration: params.integration,
    account: params.account ?? null,
    force: Boolean(params.force),
  });
}

/** The cached listing, cross-referenced with the tenant's mappings. Cache
 *  only — 409 `catalogue_not_loaded` when nothing is cached. `task_id` is the
 *  fetch the caller just watched complete. */
export async function getProducts(
  params: ProductScope & { task_id?: string },
): Promise<ProductsResponse> {
  const usp = new URLSearchParams({
    tour_operator_id: String(params.tour_operator_id),
    integration: params.integration,
  });
  if (params.account) usp.set('account', params.account);
  if (params.task_id) usp.set('task_id', params.task_id);
  return apiGet(`/ops/sync_products/products/?${usp.toString()}`);
}

// ---------------------------------------------------------------------------
// Combining products into one tour (plan 2026-10-11 product-sync joins)
// ---------------------------------------------------------------------------

/** One `joins` entry of a product-sync run: `product_id` is not built, and its
 *  IDs become additional IDs of the tour `into_product_id` creates.
 *  `move_from_tour_ids` is the operator's consent to take the IDs from those
 *  tours (D4); `move_bookings` (optional, absent = false) also moves the
 *  product's future bookings off them (D5). */
export type JoinStartItem = components['schemas']['ConsoleProductJoin'];

/** Consent to build `product_id` as its own tour although its key is an
 *  additional ID of tour `from_tour_id` (D2, "split off"). */
export type SplitOffStartItem = components['schemas']['ConsoleProductSplitOff'];

/** The Edit-booking task's two run-wide questions (D5). No defaults: the
 *  operator answers both. */
export type BookingMoveChoice = components['schemas']['ConsoleProductBookingMove'];

/** The fields every product-sync start carries, and the preview too. All three
 *  are always sent, `[]` and null included, for every integration: the setup
 *  wizard deep-merges a staged payload (an omitted key keeps its old value,
 *  lists and null replace it), so an omitted key would keep a previous run's
 *  joins, split-offs or answers, even one staged for another integration. */
export interface JoinStartFields {
  joins: JoinStartItem[];
  split_off: SplitOffStartItem[];
  /** The answers when some member moves bookings and both are given; else
   *  null (the server ignores it then). */
  booking_move: BookingMoveChoice | null;
}

/** One reason the run's joins or split-offs cannot run. `product_id` null =
 *  a problem of the whole run. `code` is the contract (`owned_elsewhere`,
 *  `primary_aliased_elsewhere`, `booking_move_bespoke`, …); render `detail`. */
export type ProductJoinProblem = components['schemas']['ConsoleProductJoinProblem'];
/** Who holds one external ID now. */
export type ProductJoinOwner = components['schemas']['ConsoleProductJoinOwner'];
/** Bookings on one tour that owns a product's IDs. */
export type ProductJoinImpact = components['schemas']['ConsoleProductJoinImpact'];
/** The server's verdict on a run's joins and split-offs. */
export type ProductJoinsPreview = components['schemas']['ConsoleProductJoinsPreview'];
export type ProductJoinsPreviewGroup = ProductJoinsPreview['groups'][number];
/** A product whose IDs join a tour: a run's member, or a catalogue `tour.join` (its preview's `member`). */
export type ProductJoinsPreviewMember = components['schemas']['ConsoleProductJoinMember'];
export type ProductJoinsPreviewExtId = components['schemas']['ConsoleProductJoinExtId'];
export type ProductSplitOffPreview = ProductJoinsPreview['split_off'][number];

export interface ProductJoinsPreviewInput extends ProductScope, JoinStartFields {
  /** The run's mapping policy; omitted, the tenant's (as the start resolves it). */
  policy?: string | null;
  /** The run's whole selection, members included. */
  product_ids: string[];
}

/** What the run's joins and split-offs would do: the same planner the start
 *  runs, so problems here are exactly what the start would refuse on (`ok`
 *  false; still a 200). Read-only. 409 `catalogue_not_loaded` without a cached
 *  listing; 400 `product_join_invalid` for a malformed request. */
export async function previewJoins(
  body: ProductJoinsPreviewInput,
  signal?: AbortSignal,
): Promise<ProductJoinsPreview> {
  return apiPost(
    '/ops/sync_products/joins/preview/',
    { ...body, account: body.account ?? null },
    signal ? { signal } : undefined,
  );
}

/** The body of a refused start (or preview): `code: product_join_invalid`
 *  with every problem at once. */
export type ProductJoinInvalid = components['schemas']['ConsoleProductJoinInvalid'];

/** The `product_join_invalid` refusal carried by `e`, or null for any other
 *  error. The start answers it as a 400 with `problems`; the caller lists them
 *  and flags the products they name. */
export function productJoinInvalid(e: unknown): ProductJoinInvalid | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const body = e.body as Partial<ProductJoinInvalid> | undefined;
  if (body?.code !== 'product_join_invalid') return null;
  const problems = Array.isArray(body.problems) ? body.problems : [];
  return { code: 'product_join_invalid', detail: body.detail ?? e.message, problems };
}

// ---------------------------------------------------------------------------
// A product-sync task's result: what happened to joins, split-offs and the
// products the re-sync guard left out (plan 2026-10-11 product-sync joins §4,
// §4a, §5, §5a). The task result (`/bgtasks/<id>/result/`) is the run's merged
// context; the spec types the keys the console reads (`ProductSyncTaskResult`).
// Every key is absent from a run that has nothing to say.
// ---------------------------------------------------------------------------

/** A product the re-sync guard left out (D2): its tour key is an additional ID
 *  of tour `tour_id`, so building it would overwrite that tour. */
export type SyncProductsOmitted = components['schemas']['ProductSyncOmitted'];

/** Who holds a value: `kind` null when only a deleted tour does. A tour's own
 *  ID that a planned move renames carries `parked_as` (`OFF/<id>`). */
export type SyncProductsHolder = components['schemas']['ProductSyncTourHolder'];

/** The build step's plan for one group of joined products (`joins_preview`):
 *  `action` is null for a value the import would skip or refuse, and for every
 *  value of a group whose primary cannot be joined (see `problems`). */
export type SyncProductsJoinPlan = components['schemas']['ProductSyncJoinPreviewGroup'];

/** One member value the import step joined onto its group's tour `tour_id`
 *  (`created`), or found there already (`already`: a re-run). */
export type SyncProductsJoinedValue = components['schemas']['ProductSyncJoinedValue'];

/** The import step's `joins` (§5): `moved` were taken from `from_tour_id` with
 *  the operator's consent (D4); `skipped` were mapped elsewhere meanwhile and
 *  left there; an error with `custom_id` null is about the whole group. */
export type SyncProductsJoinsOutcome = components['schemas']['ProductSyncJoinsOutcome'];

/** A consented split-off as the run's params carry it (the dry run's plan). */
export type SyncProductsSplitOffPlan = components['schemas']['ProductSyncSplitOffParam'];

/** The import step's `split_off` (§4a): `done`, `orphaned` (the new tour waits
 *  under `temp_key`; the next run with the same consent adopts it), `errors`. */
export type SyncProductsSplitOffOutcome = components['schemas']['ProductSyncSplitOffOutcome'];

/** One Edit-booking task the sync launched to move a moved member's bookings. */
export type SyncProductsBookingMoveLaunch = components['schemas']['ProductSyncBookingMoveLaunch'];

/** A requested booking move that launched no task (`skipped`: nothing to move;
 *  `errors`: refused or failed). */
export type SyncProductsBookingMoveIssue = components['schemas']['ProductSyncBookingMoveIssue'];

/** The keys of a product-sync result the console reads. `joins` and
 *  `split_off` hold the run's params (lists) until the import step replaces
 *  them with its outcome (objects), so a dry run still has the lists. */
export type SyncProductsResult = components['schemas']['ProductSyncTaskResult'];

/** The booking moves a run launched (D5), present when a join moves bookings. */
export type SyncProductsBookingMoves = NonNullable<SyncProductsResult['booking_moves']>;

/** Catalogue mode (Ventrata, plan §9, B8b), in the result's `post_import`: the
 *  joins of products whose `tour.join` names an imported product, written by the
 *  import as the user who saved each (`joined`), already there (`unchanged`),
 *  still waiting for the target's tour (`deferred`) or refused (`refused`, the
 *  joiner marked `join_refused`); `stopped` / `error` alone when the step did
 *  not run. */
export type SyncProductsCatalogueJoins = components['schemas']['ProductSyncCatalogueJoins'];
export type SyncProductsCatalogueJoined = components['schemas']['ProductSyncCatalogueJoined'];
export type SyncProductsCatalogueJoinRefusal = components['schemas']['ProductSyncCatalogueJoinRefusal'];

/** Catalogue mode, in `post_import`: the imported products' pending split-offs
 *  (a cleared `tour.join`), `cleared` once the key left the old tour, `pending`
 *  otherwise (the next sync retries). */
export type SyncProductsCatalogueSplitOffs = components['schemas']['ProductSyncCatalogueSplitOffs'];

// ---------------------------------------------------------------------------
// Ext-ID mappings (Additional_Ext_ID rows targeting tours)
// ---------------------------------------------------------------------------

export interface ExtIdRecord {
  id: number;
  created_ts: string | null;
  custom_id: string;
  id_type: string;
  id_type_display: string;
  use_for_matching: boolean;
  api_queryable: boolean;
  integration: string | null;
  restech_account: string | null;
  tour: { id: number; name: string | null };
}

export const EXT_ID_TYPES: { value: string; label: string }[] = [
  { value: 'product_id', label: 'Product ID' },
  { value: 'package_id', label: 'Package ID' },
  { value: 'option_id', label: 'Option ID (Ventrata)' },
  { value: 'product_with_departure_time', label: 'Product ID + departure time' },
  { value: 'product_with_departure_weekday', label: 'Product ID + departure weekday' },
  {
    value: 'product_with_departure_weekday_and_time',
    label: 'Product ID + departure weekday and time',
  },
  { value: 'rate_id', label: 'Product ID + rate (Bokun)' },
  { value: 'other', label: 'Other / templated' },
];

export async function listExtIds(
  toId: number,
  params: { integration?: string; account?: string; tour_id?: number; search?: string } = {},
): Promise<{ results: ExtIdRecord[] }> {
  const usp = new URLSearchParams();
  if (params.integration) usp.set('integration', params.integration);
  if (params.account) usp.set('account', params.account);
  if (params.tour_id) usp.set('tour_id', String(params.tour_id));
  if (params.search) usp.set('search', params.search);
  const qs = usp.toString();
  return apiGet(`/tour_operators/${toId}/ext_ids/${qs ? `?${qs}` : ''}`);
}

export interface ExtIdWriteInput {
  tour_id?: number;
  custom_id?: string;
  id_type?: string;
  integration?: string | null;
  restech_account?: string | null;
  api_queryable?: boolean;
  use_for_matching?: boolean;
}

export async function createExtId(toId: number, body: ExtIdWriteInput): Promise<ExtIdRecord> {
  return apiPost(`/tour_operators/${toId}/ext_ids/`, body);
}

export async function updateExtId(
  toId: number,
  pk: number,
  body: ExtIdWriteInput,
): Promise<ExtIdRecord> {
  return apiPatch(`/tour_operators/${toId}/ext_ids/${pk}/`, body);
}

export async function deleteExtId(toId: number, pk: number): Promise<void> {
  return apiDelete(`/tour_operators/${toId}/ext_ids/${pk}/`);
}

// ---------------------------------------------------------------------------
// Tour autocomplete
// ---------------------------------------------------------------------------

export interface TourOption {
  id: number;
  name: string;
  custom_id: string | null;
}

export async function searchTours(
  toId: number,
  query: string,
  signal?: AbortSignal,
): Promise<TourOption[]> {
  const r = await apiGet<{ results: TourOption[] }>(
    `/tour_operators/${toId}/tours/?query=${encodeURIComponent(query)}`,
    { signal },
  );
  return r.results;
}

// ---------------------------------------------------------------------------
// Booking-sync targets (two-level tour → external-ID picker)
// ---------------------------------------------------------------------------

export interface SyncBookingsTarget {
  tour: { id: number; name: string };
  /** The tour's own custom_id — always selectable when non-empty. */
  primary: string | null;
  ext_ids: { pk: number; value: string; id_type: string }[];
}

export async function fetchSyncTargets(params: {
  tour_operator_id: number;
  integration: string;
  account?: string | null;
  query?: string;
}): Promise<{ results: SyncBookingsTarget[] }> {
  const usp = new URLSearchParams({
    tour_operator_id: String(params.tour_operator_id),
    integration: params.integration,
  });
  if (params.account) usp.set('account', params.account);
  if (params.query) usp.set('query', params.query);
  return apiGet(`/ops/sync_bookings/targets/?${usp.toString()}`);
}

/** One option of a `select` launch argument.
 *
 *  `is_company` / `hint` are FareHarbor's answer to a distinction the label
 *  cannot show: an alias from a dict `user_key` IS a company shortname and so a
 *  walkable `companies/<X>/` path segment, while an account slug standing in for
 *  a bare-string key resolves to no company at all. Both stay offered — a bare
 *  key authenticates, so booking sync works either way — but only one of them
 *  can be a product catalogue. Optional: every other integration omits them.
 *
 *  Declared in `console.openapi.yaml` on the `SyncExtraArg` choice item; this
 *  interface mirrors it, as every other type in this module does. */
export interface SyncArgChoice {
  value: string;
  label: string;
  is_company?: boolean;
  /** Set only on a non-company entry: what it actually is, and what it cannot do. */
  hint?: string;
}

/** Integration-specific launch argument (SYNC_EXTRA_ARGS), e.g. FareHarbor's
 *  required Company selector. */
export interface SyncExtraArg {
  name: string;
  label: string;
  type: 'select' | 'text';
  required: boolean;
  choices?: SyncArgChoice[];
}

export async function fetchSyncExtraArgs(params: {
  tour_operator_id: number;
  integration: string;
  account?: string | null;
}): Promise<{ extra_args: SyncExtraArg[] }> {
  const usp = new URLSearchParams({
    tour_operator_id: String(params.tour_operator_id),
    integration: params.integration,
  });
  if (params.account) usp.set('account', params.account);
  return apiGet(`/ops/sync_bookings/extra_args/?${usp.toString()}`);
}
